import { useState, useMemo } from "react";
import {
  Box, Stack, Typography, Autocomplete, TextField, Slider,
  ToggleButtonGroup, ToggleButton, Divider, Button, Collapse,
  Select, MenuItem, FormControl, InputLabel, OutlinedInput,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { SEV_COLOR, TYPE_COLOR, QUICK_PORTS, QUICK_FILTERS, scoreColor } from "../constants";
import Tooltip from "@mui/material/Tooltip";

export default function BehaviorsFiltersBar({
  anomalies = [],
  // server-side — richiedono "Applica"
  selectedIps, setSelectedIps,
  filterPorts, setFilterPorts,
  filterExcludedIps, setFilterExcludedIps,
  filterExcludedPorts, setFilterExcludedPorts,
  hours, setHours,
  onApplyFilters,
  // client-side — applicate in tempo reale
  filterDstIps, setFilterDstIps,
  filterSeverity, setFilterSeverity,
  filterTypes, setFilterTypes,
  minScore, setMinScore,
  quickFilters = [], setQuickFilters,
  quickPortsActive, onToggleQuickPorts,
}) {
  const [open, setOpen] = useState(true);

  const srcIpOptions = useMemo(() => {
    const s = new Set(anomalies.map(a => a.entity?.id).filter(Boolean));
    return [...s].sort();
  }, [anomalies]);

  const dstIpOptions = useMemo(() => {
    const s = new Set();
    anomalies.forEach(a => (a.graph?.edges ?? []).forEach(e => e.dst && s.add(e.dst)));
    return [...s].sort();
  }, [anomalies]);

  const allTypes = useMemo(() => {
    const s = new Set();
    anomalies.forEach(a => (a.anomaly_types ?? []).forEach(t => s.add(t)));
    return [...s].sort();
  }, [anomalies]);

  const portOptions = useMemo(() => {
    const s = new Set();
    anomalies.forEach(a => {
      (a.graph?.source?.reasons ?? []).forEach(r => (r.ports ?? []).forEach(p => s.add(String(p))));
      (a.graph?.edges ?? []).forEach(e =>
        (e.reasons ?? []).forEach(r => (r.ports ?? []).forEach(p => s.add(String(p))))
      );
    });
    return [...s].sort((a, b) => Number(a) - Number(b));
  }, [anomalies]);

  const allIps = useMemo(() => {
    const s = new Set([...srcIpOptions, ...dstIpOptions]);
    return [...s].sort();
  }, [srcIpOptions, dstIpOptions]);

  const activeCount =
    (filterSeverity?.length ?? 0) +
    (filterTypes?.length ?? 0) +
    (filterDstIps?.length ?? 0) +
    (minScore > 0 ? 1 : 0) +
    (quickFilters?.length ?? 0) +
    (selectedIps?.length ?? 0) +
    (filterPorts?.length ?? 0) +
    (filterExcludedIps?.length ?? 0) +
    (filterExcludedPorts?.length ?? 0);

  const acProps = { size: "small", multiple: true, limitTags: 2 };

  return (
    <Box sx={{ bgcolor: "#1e1e1e", border: "1px solid #2e2e2e", borderRadius: 1.5, mb: 2 }}>
      {/* ── Header collassabile ── */}
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        onClick={() => setOpen(v => !v)}
        sx={{ px: 1.5, py: 1, cursor: "pointer", userSelect: "none" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <FilterListIcon sx={{ fontSize: 15, color: "#666" }} />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Filters</Typography>
          {activeCount > 0 && (
            <Box sx={{ px: 0.7, py: 0.1, bgcolor: "#45909530", borderRadius: 0.5 }}>
              <Typography sx={{ fontSize: 9, color: "#459095" }}>{activeCount} active</Typography>
            </Box>
          )}
        </Stack>
        <ExpandMoreIcon sx={{
          fontSize: 16, color: "#555",
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }} />
      </Stack>

      {/* ── Filtri rapidi: sempre visibili, anche a pannello chiuso ── */}
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.8} sx={{ px: 1.5, pb: 1 }}>
        <Typography sx={{ fontSize: 10, color: "text.secondary", mr: 0.3 }}>Quick filters</Typography>
        {QUICK_FILTERS.map(({ key, label, tip }) => {
          const active = quickFilters.includes(key);
          return (
            <Tooltip key={key} title={tip} placement="top" arrow>
              <Box
                role="button"
                aria-pressed={active}
                tabIndex={0}
                onClick={() => setQuickFilters(active ? quickFilters.filter((k) => k !== key) : [...quickFilters, key])}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setQuickFilters(active ? quickFilters.filter((k) => k !== key) : [...quickFilters, key]);
                  }
                }}
                sx={{
                  px: 1, py: 0.25, borderRadius: 4, cursor: "pointer", userSelect: "none", outline: "none",
                  border: `1px solid ${active ? "#459095" : "#333"}`,
                  bgcolor: active ? "#45909525" : "transparent",
                  "&:hover, &:focus-visible": { borderColor: "#459095" },
                }}
              >
                <Typography sx={{ fontSize: 11, color: active ? "#459095" : "text.secondary", fontWeight: active ? 700 : 400 }}>{label}</Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 1.5, pt: 1.2, pb: 1.5 }}>

          {/* ── Riga 1 – Severity + Anomaly type ── */}
          <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start"
            sx={{ mb: 1.2, width: "100%" }}>

            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 0.4 }}>Severity</Typography>
              <ToggleButtonGroup size="small" value={filterSeverity}
                onChange={(_, v) => setFilterSeverity(v)}>
                {["high", "medium", "low", "info"].map(s => (
                  <ToggleButton key={s} value={s} sx={{
                    px: 1, py: 0.3, fontSize: 10, textTransform: "none", color: SEV_COLOR[s],
                    "&.Mui-selected": { bgcolor: `${SEV_COLOR[s]}22`, color: SEV_COLOR[s], borderColor: SEV_COLOR[s] },
                  }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: SEV_COLOR[s], mr: 0.5 }} />
                    {s}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {allTypes.length > 0 && (
              <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel sx={{ fontSize: 11 }}>Anomaly type</InputLabel>
                <Select
                  multiple
                  value={filterTypes ?? []}
                  onChange={(e) => setFilterTypes(e.target.value)}
                  input={<OutlinedInput label="Anomaly type" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3, py: 0.2 }}>
                      {selected.map(t => {
                        const c = TYPE_COLOR[t] ?? "#aaaaaa";
                        return (
                          <Box key={t} sx={{
                            px: 0.6, py: 0.1,
                            bgcolor: `${c}22`, border: `1px solid ${c}88`, borderRadius: 0.5,
                          }}>
                            <Typography sx={{ fontSize: 9, color: c }}>{t}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: { bgcolor: "#1e1e1e", border: "1px solid #333", boxShadow: "0 4px 24px #0008" },
                    },
                  }}
                >
                  {allTypes.map(t => {
                    const c = TYPE_COLOR[t] ?? "#aaaaaa";
                    const sel = (filterTypes ?? []).includes(t);
                    return (
                      <MenuItem key={t} value={t} sx={{
                        fontSize: 12, py: 0.8,
                        bgcolor: sel ? `${c}18` : "transparent",
                        "&:hover": { bgcolor: `${c}22` },
                        "&.Mui-selected": { bgcolor: `${c}18` },
                        "&.Mui-selected:hover": { bgcolor: `${c}28` },
                      }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c, mr: 1.5, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: c }}>{t}</Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* ── Riga 2 – IP dst + score ── */}
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="flex-end"
            sx={{ mb: 1.2, width: "100%" }}>

            <Autocomplete {...acProps} options={dstIpOptions} value={filterDstIps ?? []}
              onChange={(_, v) => setFilterDstIps(v)} freeSolo
              sx={{ flex: 1, minWidth: 160 }}
              renderInput={p => <TextField {...p} label="Destination IP" />} />

            <Box sx={{ flex: "0 0 220px" }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Min score</Typography>
                <Typography sx={{ fontSize: 10, fontFamily: "monospace", color: scoreColor(minScore) }}>
                  {(minScore ?? 0).toFixed(2)}
                </Typography>
              </Stack>
              <Slider value={minScore ?? 0} onChange={(_, v) => setMinScore(v)}
                min={0} max={1} step={0.05} size="small"
                sx={{
                  color: scoreColor(minScore), py: 0.8,
                  "& .MuiSlider-thumb": { bgcolor: scoreColor(minScore) },
                  "& .MuiSlider-track": { bgcolor: scoreColor(minScore), borderColor: scoreColor(minScore) },
                }} />
            </Box>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {/* ── Riga 3 – filtri server-side ── */}
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="flex-end"
            sx={{ width: "100%" }}>

            <Autocomplete {...acProps} options={srcIpOptions} value={selectedIps ?? []}
              onChange={(_, v) => setSelectedIps?.(v)}
              sx={{ flex: 1, minWidth: 160 }}
              renderInput={p => <TextField {...p} label="Source IP" />} />

            <Autocomplete {...acProps} options={allIps} value={filterExcludedIps ?? []}
              onChange={(_, v) => setFilterExcludedIps?.(v)}
              sx={{ flex: 1, minWidth: 160 }}
              renderInput={p => <TextField {...p} label="Excluded IPs" />} />

            <Autocomplete {...acProps} options={portOptions} value={(filterPorts ?? []).map(String)}
              onChange={(_, v) => setFilterPorts?.(v.map(Number))} freeSolo
              sx={{ flex: 1, minWidth: 140 }}
              renderInput={p => <TextField {...p} label="Ports" />} />

            <Box
              onClick={() => onToggleQuickPorts?.()}
              title={`Common ports: ${QUICK_PORTS.join(", ")}`}
              sx={{
                px: 1, py: 0.6, cursor: "pointer", borderRadius: 0.6,
                fontSize: 10, userSelect: "none", whiteSpace: "nowrap", flexShrink: 0,
                bgcolor: quickPortsActive ? "#45909522" : "transparent",
                border: `1px solid ${quickPortsActive ? "#459095" : "#444"}`,
                color: quickPortsActive ? "#459095" : "text.secondary",
                "&:hover": { borderColor: "#459095" },
              }}
            >
              Common ports
            </Box>

            <Autocomplete {...acProps} options={portOptions} value={(filterExcludedPorts ?? []).map(String)}
              onChange={(_, v) => setFilterExcludedPorts?.(v.map(Number))} freeSolo
              sx={{ flex: 1, minWidth: 140 }}
              renderInput={p => <TextField {...p} label="Excluded ports" />} />

            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ fontSize: 10, color: "text.secondary", mb: 0.4 }}>Time window</Typography>
              <ToggleButtonGroup size="small" exclusive value={hours}
                onChange={(_, v) => v && setHours?.(v)}>
                {["24", "48", "72"].map(h => (
                  <ToggleButton key={h} value={h} sx={{
                    px: 1, py: 0.3, fontSize: 10, textTransform: "none",
                    "&.Mui-selected": { bgcolor: "#45909522", color: "#459095", borderColor: "#459095" },
                  }}>{h}h</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Button variant="outlined" size="small" onClick={() => onApplyFilters?.()}
              sx={{
                ml: "auto", fontSize: 11, textTransform: "none", flexShrink: 0,
                borderColor: "#459095", color: "#459095",
                "&:hover": { borderColor: "#459095", bgcolor: "#45909515" },
              }}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
